import React, { useMemo, useState } from 'react'
import { EmptyState } from '@enamelai/design-system'
import {
  EMPTY_STATE_PREVIEW_STATE_LABELS,
  EMPTY_STATE_PREVIEW_STATES,
  EmptyStatePreviewState,
} from '../showcaseConstants'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './EmptyStateShowcase.css'

const EmptyStateShowcase: React.FC = () => {
  const [title, setTitle] = useState('Empty page sample')
  const [description, setDescription] = useState("There's no data to show here")
  const [iconClass, setIconClass] = useState('')
  const [actionLabel, setActionLabel] = useState('Button')
  const [showAction, setShowAction] = useState(true)
  const [highlightedState, setHighlightedState] =
    useState<EmptyStatePreviewState>('default')

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'empty-state-highlight',
            label: 'Highlighted state',
            control: (
              <select
                id="empty-state-highlight"
                className="ui-field-control"
                value={highlightedState}
                onChange={(event) =>
                  setHighlightedState(
                    event.target.value as EmptyStatePreviewState,
                  )
                }
              >
                {EMPTY_STATE_PREVIEW_STATES.map((option) => (
                  <option key={option} value={option}>
                    {EMPTY_STATE_PREVIEW_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'empty-state-title',
            label: 'Title',
            control: (
              <input
                id="empty-state-title"
                className="ui-field-control"
                type="text"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
              />
            ),
          },
          {
            id: 'empty-state-description',
            label: 'Description',
            control: (
              <input
                id="empty-state-description"
                className="ui-field-control"
                type="text"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            ),
          },
          {
            id: 'empty-state-icon',
            label: 'Icon class (optional)',
            control: (
              <input
                id="empty-state-icon"
                className="ui-field-control"
                type="text"
                value={iconClass}
                placeholder="Default: Files1"
                onChange={(event) => setIconClass(event.target.value)}
              />
            ),
          },
        ],
      },
      {
        title: 'Action',
        fields: [
          {
            id: 'empty-state-show-action',
            label: 'Show action',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="empty-state-show-action"
                  type="checkbox"
                  checked={showAction}
                  onChange={(event) => setShowAction(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'empty-state-action-label',
            label: 'Action label',
            control: (
              <input
                id="empty-state-action-label"
                className="ui-field-control"
                type="text"
                value={actionLabel}
                disabled={!showAction}
                onChange={(event) => setActionLabel(event.target.value)}
              />
            ),
          },
        ],
      },
    ],
    [actionLabel, description, highlightedState, iconClass, showAction, title],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Empty state"
        description="Centered Files icon, title, description, and primary Md button."
        stageClassName="ui-preview__stage--wide empty-state-showcase__stage-wrap"
      >
        <div className={`empty-state-showcase__sheet`}>
          <section className={`ui-showcase-live`}>
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Fills the section. Title 18/28, description 12/20, Files icon
              88px, primary Md button.
            </p>
            <div className="empty-state-showcase__live">
              <EmptyState
                title={title}
                description={description}
                iconClass={iconClass || undefined}
                actionLabel={showAction ? actionLabel : undefined}
                onAction={
                  showAction
                    ? () => {
                        // Showcase-only no-op
                      }
                    : undefined
                }
              />
            </div>
          </section>

          <section className="empty-state-showcase__matrix">
            <h3 className="ui-showcase-live__title">Surfaces</h3>
            <p className="ui-showcase-live__note">
              Empty state on the default page surface (`blue-100`).
            </p>
            <div className="empty-state-showcase__box">
              <EmptyState
                title={title}
                description={description}
                actionLabel={showAction ? actionLabel : undefined}
                onAction={
                  showAction
                    ? () => {
                        // Showcase-only no-op
                      }
                    : undefined
                }
              />
            </div>
            <div className="empty-state-showcase__box">
              <EmptyState
                title={title}
                description={description}
                actionLabel={showAction ? actionLabel : undefined}
                onAction={
                  showAction
                    ? () => {
                        // Showcase-only no-op
                      }
                    : undefined
                }
              />
            </div>
          </section>

          <section className="empty-state-showcase__matrix">
            <h3 className="ui-showcase-live__title">States</h3>
            {EMPTY_STATE_PREVIEW_STATES.map((state) => (
              <div
                key={state}
                className={`empty-state-showcase__row${
                  highlightedState === state
                    ? ' empty-state-showcase__row--selected'
                    : ''
                }`}
              >
                <h4 className="empty-state-showcase__label">
                  {EMPTY_STATE_PREVIEW_STATE_LABELS[state]}
                </h4>
                <div className="empty-state-showcase__box">
                  <EmptyState
                    title={
                      state === 'no-action'
                        ? 'Empty page sample'
                        : 'Empty page sample'
                    }
                    description="There's no data to show here"
                    actionLabel={state === 'no-action' ? undefined : 'Button'}
                    onAction={
                      state === 'no-action'
                        ? undefined
                        : () => {
                            // Showcase-only no-op
                          }
                    }
                  />
                </div>
              </div>
            ))}
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Empty state"
        sections={propertySections}
      />
    </>
  )
}

export default EmptyStateShowcase
